/**
 * Personal agents: an agent that works for one member and sorts their private triage area. A member makes an agent they
 * set up theirs; an admin makes any agent anyone's. The member and admins take it away.
 */
import type { Member, PersonalAgent, Role } from "../api/types.gen.ts";
import { atLeast } from "./roles.ts";

/** Who `agent` works for, if anyone. */
export function personalOf(links: readonly PersonalAgent[] | undefined, agent: string): PersonalAgent | undefined {
  return links?.find((l) => l.agent === agent);
}

/** The agents of `members` that work for nobody yet. */
export function freeAgents(members: readonly Member[], links: readonly PersonalAgent[]): Member[] {
  return members.filter((m) => m.kind === "agent" && !personalOf(links, m.id));
}

/** The agents of `members` working for someone other than `me`, with whom: theirs until they let them go. */
export function takenAgents(
  members: readonly Member[],
  links: readonly PersonalAgent[],
  me: string,
): { agent: Member; person: string }[] {
  return members.flatMap((m) => {
    const l = m.kind === "agent" ? personalOf(links, m.id) : undefined;
    return l && l.person !== me ? [{ agent: m, person: l.person_name }] : [];
  });
}

/**
 * The people `role` (yours) may make `agent` work for: anyone in the org (admins), else yourself, when you set it up
 * (without `agent`: any agent you set up).
 */
export function grantableTo(members: readonly Member[], me: string, role: Role, agent?: Member): Member[] {
  if (atLeast(role, "admin")) {
    return members.filter((m) => m.kind === "human");
  }
  const yours = agent === undefined || agent.set_up_by === me;
  return members.filter((m) => m.kind === "human" && m.id === me && yours);
}

/** Whether you (`me`, with `role`) may take `link` away: it's yours, or you're an admin. */
export function canRevoke(link: PersonalAgent, me: string | undefined, role: Role): boolean {
  return link.person === me || atLeast(role, "admin");
}

/** What can be done about an agent's personal link from its row: make it someone's, or take it away. */
export type PersonalAction = "make" | "remove";

/**
 * What you (`me`, with `role`) may do about `agent`'s personal link, among `members` and the org's `links`: take it
 * away (it's yours, or you're an admin), or make it someone's (yours, if you set it up; admins anyone's); none: neither.
 */
export function personalAction(
  agent: Member,
  o: {
    members: readonly Member[];
    links: readonly PersonalAgent[] | undefined;
    me: string | undefined;
    role: Role;
  },
): PersonalAction | null {
  const { members, links, me, role } = o;
  if (agent.kind !== "agent" || !links || me === undefined) {
    return null;
  }
  const link = personalOf(links, agent.id);
  if (link) {
    return canRevoke(link, me, role) ? "remove" : null;
  }
  return grantableTo(members, me, role, agent).length > 0 ? "make" : null;
}

/** The agents working for `me`, by name. */
export function mine(links: readonly PersonalAgent[], me: string): PersonalAgent[] {
  return links.filter((l) => l.person === me).sort((a, b) => a.agent_name.localeCompare(b.agent_name));
}
